import { z } from "zod";

/**
 * The patient's declaration before a client submits a request. Versioned: the
 * server records which version was accepted (with time and IP), as the privacy
 * policy (section 2.5) commits to. Changing the wording = a new version.
 */
export const DECLARATION_VERSION = "2026-10-30";

export const DECLARATION_SECTIONS: ReadonlyArray<{ title: string; items: readonly string[] }> = [
	{
		title: "זהות ומסמכים",
		items: [
			"אני המטופל/ת שעבורו/ה מוגשת הבקשה, או אפוטרופוס/ית חוקי/ת שלו/ה (ובמקרה זה אצרף אישור אפוטרופסות).",
			"כל המסמכים שהעליתי אמיתיים, מלאים, לא נערכו ולא זויפו, ושייכים לי. אני יודע/ת שהגשת מסמך מזויף היא עבירה פלילית ותוביל לסגירת התיק ולדיווח לרשויות.",
			"מסרתי את כל המידע הרפואי הרלוונטי, כולל מחלות, תרופות, אשפוזים וטיפולים קודמים. ידוע לי שהסתרת מידע עלולה להביא לדחיית הבקשה או לביטול הרישיון, ולא תזכה בהחזר.",
			"הפרטים שהזנתי (שם, ת\"ז, טלפון, כתובת מייל) נכונים ומעודכנים.",
		],
	},
	{
		title: "מהות השירות",
		items: [
			"קנאקטיב אינה גורם רפואי ואינה משרד הבריאות. השירות הוא בדיקת התיק, הכנתו והגשתו לרופא. ההחלטה אם להמליץ על רישיון היא של הרופא בלבד, לפי שיקול דעתו הרפואי, וההחלטה אם להנפיק רישיון היא של משרד הבריאות בלבד.",
			"ידוע לי שאין הבטחה לאישור הבקשה, ושאפשר שהרופא יבקש מידע נוסף, יסרב להמליץ, או ימליץ על טיפול/מינון שונה מהמבוקש.",
		],
	},
	{
		title: "תשלום",
		items: [
			"התשלום הוא עבור שירות הטיפול בתיק והגשתו — לא עבור הרישיון עצמו. השירות מתחיל מיד עם התשלום.",
			"לא יינתן החזר כספי בשום מקרה, לרבות אם הבקשה נדחתה על ידי הרופא או משרד הבריאות, אם אבחר להפסיק את ההליך, או אם לא אשלים מסמכים שיתבקשו.",
		],
	},
	{
		title: "זמנים",
		items: [
			"זמן הטיפול הוא עד 30 ימי עסקים מרגע התשלום והשלמת כל המסמכים. ימים שבהם התיק ממתין להשלמה מצדי אינם נספרים. הזמן תלוי גם בגורמים חיצוניים (רופא, משרד הבריאות) ואינו מובטח.",
			"אם אתבקש להשלים מסמכים ולא אעשה זאת תוך 14 יום, התיק ייסגר ללא החזר.",
		],
	},
	{
		title: "קבלת הרישיון",
		items: [
			"הרישיון יישלח על ידי משרד הבריאות לכתובת המייל שהזנתי. באחריותי לוודא שהכתובת נכונה, לבדוק גם בתיקיית הספאם, ולשמור את הרישיון.",
			"אם הרישיון לא הגיע בגלל כתובת שגויה או בעיה בתיבת המייל שלי — הטיפול מול משרד הבריאות הוא באחריותי.",
		],
	},
	{
		title: "שמירת מידע ומסמכים",
		items: [
			"אני מסכים/ה שהמסמכים שהעליתי (לרבות צילום תעודת זהות, מסמכים רפואיים, ההמלצה הרפואית והרישיון) יישמרו במערכת קנאקטיב למשך 7 שנים לצורך מתן השירות, חידושים עתידיים ועמידה בדרישות החוק, ויועברו לרופא המטפל, לגורם שמנהל את הרופאים ולמשרד הבריאות לצורך הבקשה בלבד.",
		],
	},
];

/** The last item, rendered with links to the terms and privacy pages. */
export const DECLARATION_TERMS_ITEM = {
	before: "קראתי את ",
	terms: "תנאי השימוש",
	between: " ואת ",
	privacy: "מדיניות הפרטיות",
	after: " ואני מסכים/ה להם.",
} as const;

export const DECLARATION_ACCEPT_LABEL = "קראתי ואני מאשר/ת את כל האמור לעיל";
export const RENEWAL_CONTACT_CONSENT_TEXT =
	"אני מאשר/ת לקנאקטיב ליצור איתי קשר לפני מועד חידוש הרישיון הבא ולשלוח לי עדכונים.";

/** Saved on the request when the client submits it. The authoritative record —
 *  with server time and IP — is consents/{id}, written by the server. */
export const RequestDeclarationSchema = z.object({
	version: z.string(),
	acceptedAt: z.number(),
	renewalContactConsent: z.boolean(),
});
export type TRequestDeclaration = z.infer<typeof RequestDeclarationSchema>;
